import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { MarkDoneModalComponent } from '../shared/calendar/app-mark-done-modal/app-mark-done-modal.component';
import { ActivityModalDataService } from './lead-activity-modal.service';
import { tasksServices } from './tasks.service';
import { leadsServices } from './lead.service';
import { stepService } from './step.service';
import { generalService } from './general-info';
import { NotificationsService } from './notifications/notifications.service';

@Injectable({
  providedIn: 'root'
})
export class ActivityCompletionService {

  constructor(
    private dialog: MatDialog,
    private taskService: tasksServices,
    private modalDataService: ActivityModalDataService,
    private leadsService: leadsServices,
    private stepService: stepService,
    private generalService: generalService,
    private notificationService:NotificationsService
  ) {}

  /**
   * Open Create Activity modal with a prefilled lead
   * Called when an outcome with ask_for_new_activity is selected
   */
  async openCreateActivityForLead(lead: any): Promise<void> {
    // Lazy load to avoid circular dependency
    const { EventConfirmationDialogComponent } = await import(
      '../shared/calendar/event-confirmation-dialog/event-confirmation-dialog.component'
    );

    const startDate = new Date();
    const endDate = new Date(startDate.getTime() + 30 * 60 * 1000); // 30 minutes later

    const newActivityData = {
      lead: lead,
      defaultLead: lead?.id,
      start: startDate,
      end: endDate,
      listView: true,
      hourSegments: 30,
      detailed: true,
      editMode: false,
      isModal: true
    };

    this.dialog.open(EventConfirmationDialogComponent, {
      position: { top: '0' },
      height: '95%',
      maxHeight: '900px',
      width: '60%',
      minWidth: '400px',
      maxWidth: '800px',
      enterAnimationDuration: '150ms',
      exitAnimationDuration: '100ms',
      panelClass: 'activity-dialog',
      data: newActivityData
    });
  }


  /**
   * Enroll a lead with a start date
   * Called when an outcome with enroll_lead is selected
   * Uses existing lead-step endpoint
   */
  async enrollLead(leadId: number, startAt: string, note?: string, faAdvisorId?: number | null): Promise<boolean> {
    try {
      // Get lead info to find pipeline and current step
      const lead = await firstValueFrom(this.leadsService.getOne(leadId));
      if (!lead?.step?.pipeline_id) {
        console.error('Lead has no pipeline assigned');
        return false;
      }

      // Get steps for the pipeline to find the enrolled step
      const steps: any[] = await firstValueFrom(
        this.stepService.get({ id: lead.step.pipeline_id }, 'by-pipeline')
      );

      const enrolledStep = steps?.find((s: any) => s.step_type === 'enrolled');
      if (!enrolledStep) {
        console.error('No enrolled step found in pipeline');
        return false;
      }

      // Call existing lead-step endpoint
      const payload: any = {
        current_step_id: enrolledStep.id,
        leadId: leadId,
        step_type: 'enrolled',
        prevId: lead.current_step_id,
        start_at: startAt
      };

      if (note) {
        payload.note = note;
      }

      payload.fa_advisor_id = faAdvisorId ?? null;

      const response = await firstValueFrom(
        this.generalService.put(payload, leadId, 'lead-step')
      );

      if (response) {
        const updatedLead = await firstValueFrom(this.leadsService.getOne(leadId));
        this.modalDataService.emitLeadUpdate(updatedLead);
        return true;
      }
      return false;
    } catch (error: any) {
      const msg = error?.error?.message || error?.message || 'Failed to enroll lead';
      console.error('Error enrolling lead:', msg);
      this.notificationService.showNonErrorSnackBar(msg, 3000);
      return false;
    }
  }

  async markActivityAsDone(activity: any, eventForm?: any): Promise<any> {
    // Check if we need to get the activity type information
    let activityType = activity?.type;

    // If we don't have type info but have type_id, you might need to fetch it
    // This is assuming you have access to the activity types cache
    if (!activityType && activity?.type_id) {
      // You could inject activityTypeCache service and fetch the type
      // For now, we'll assume the type is passed properly
      console.warn('Activity type not found, this may cause issues with appointment detection');
    }

    const isAppointment = activityType?.is_appointment || activity.appointment?.length || false;
    const isMarkedComplete = eventForm ? !eventForm.value.completed : !activity.completed;

    // Recurring activity: ask which scope to apply only when marking-as-done.
    // Uncompleting is a plain completed=false toggle and should not prompt.
    // Direct-API path only — the edit dialog handles its own scope prompt.
    let recurrenceScope: string | null = null;
    if (!eventForm && isMarkedComplete && activity?.recurrence_rule_id) {
      const { RecurrenceScopeDialogComponent } = await import(
        '../shared/calendar/recurrence-scope-dialog/recurrence-scope-dialog.component'
      );
      const scopeRef = this.dialog.open(RecurrenceScopeDialogComponent, {
        width: '400px',
        data: { action: 'edit' },
      });
      recurrenceScope = await firstValueFrom(scopeRef.afterClosed());
      if (!recurrenceScope) {
        // User cancelled — caller reverts the checkbox on null return
        return null;
      }
    }
    
    // Case 1: Un-marking as done (going from completed to not completed)
    if (!isMarkedComplete) {
      if (eventForm) {
        // For form-based components
        eventForm.patchValue({
          completed: false,
          status: activity?.type?.is_appointment ? 'SCHEDULED' : activity.status
        });
        return { formUpdated: true };
      } else {
        // For direct task updates
        const response = await firstValueFrom(
          this.taskService.patch({
            completed: false,
            attendanceStatus: isAppointment ? 'SCHEDULED' : undefined,
            ...(recurrenceScope ? { recurrenceScope } : {})
          }, activity.id)
        );
        this.modalDataService.emitEvent('update', response.data);
        return response;
      }
    }
    
    // Case 2: Marking as done
    if (isAppointment && (!activity.attendanceStatus || activity.attendanceStatus === 'SCHEDULED')) {
      // Show modal for appointments
      const dialogRef = this.dialog.open(MarkDoneModalComponent, {
        width: '500px',
        disableClose: false,
        data: { activity }
      });
      
      const result = await firstValueFrom(dialogRef.afterClosed());
      console.log('📋 MarkDoneModal result:', result);

      if (result) {
        // Handle RESCHEDULED: call reschedule API instead of patch
        if (result.status === 'RESCHEDULED' && result.rescheduleData) {
          return this.handleReschedule(activity, result.rescheduleData, eventForm);
        }

        // ═══ VALIDATE mark_lead_as_lost BEFORE completing the activity ═══
        // If this outcome marks the lead as lost, try the API first.
        // If it fails (e.g. Won lead), block the entire completion.
        if (result.mark_lead_as_lost && result.lead?.id && result.lost_reason_id) {
          const lostSuccess = await this.markLeadAsLost(result.lead.id, result.lost_reason_id);
          if (!lostSuccess) {
            // Backend rejected (Won lead, etc.) — DO NOT complete the activity
            // User needs to select a different outcome
            return null;
          }
        }

        console.log(result,'afsfaaffaafsafs')
        // ═══ VALIDATE enroll_lead BEFORE completing the activity ═══
        let enrollSuccess = true;
        if (result.enroll_lead && result.lead?.id && result.enrollmentData) {
          enrollSuccess = await this.enrollLead(result.lead.id, result.enrollmentData.start_at, result.enrollmentData.note, result.enrollmentData.fa_advisor_id);
          if (!enrollSuccess) {
            return null;
          }
        }

        const updateData: any = {
          completed: true,
          attendanceStatus: result.status
        };

        // Include outcome_id if provided
        if (result.outcome_id) {
          updateData.outcome_id = result.outcome_id;
        }

        if (recurrenceScope) {
          updateData.recurrenceScope = recurrenceScope;
        }

        if (eventForm) {
          // Update form
          eventForm.patchValue({
            completed: true,
            status: result.status
          });

          // Open Create Activity modal if ask_for_new_activity is enabled
          if (result.ask_for_new_activity && result.lead) {
            setTimeout(() => {
              this.openCreateActivityForLead(result.lead);
            }, 300);
          }

          // mark_lead_as_lost already handled above (before completion)
          // enroll_lead already handled above (before completion)

          return {
            formUpdated: true,
            status: result.status,
            outcome_id: result.outcome_id
          };
        } else {
          // Direct API update
          const response = await firstValueFrom(
            this.taskService.patch(updateData, activity.id)
          );
          const isBulkScope =
            recurrenceScope === 'THIS_AND_FOLLOWING' ||
            recurrenceScope === 'ALL_EVENTS';
          const eventPayload = isBulkScope
            ? { ...response.data, _recurrenceScope: recurrenceScope }
            : response.data;
          this.modalDataService.emitEvent(
            isBulkScope ? 'bulk-update' : 'update',
            eventPayload,
          );

          // Open Create Activity modal if ask_for_new_activity is enabled
          if (result.ask_for_new_activity && result.lead) {
            setTimeout(() => {
              this.openCreateActivityForLead(result.lead);
            }, 300);
          }

          // mark_lead_as_lost already handled above (before completion)
          // enroll_lead already handled above (before completion)

          return { ...response, bulkRecurrenceUpdate: isBulkScope };
        }
      }
      // User cancelled the modal
      return null;
    } else {
      // For tasks or appointments already with status
      if (eventForm) {
        eventForm.patchValue({
          completed: true,
          status: isAppointment && activity.status === 'SCHEDULED' ? 'SHOW' : activity.status
        });
        return { formUpdated: true };
      } else {
        const updateData: any = {
          completed: true,
          attendanceStatus: isAppointment && activity.status === 'SCHEDULED' ? 'SHOW' : activity.attendanceStatus
        };

        if (recurrenceScope) {
          updateData.recurrenceScope = recurrenceScope;
        }

        const response = await firstValueFrom(
          this.taskService.patch(updateData, activity.id)
        );
        const isBulkScope =
          recurrenceScope === 'THIS_AND_FOLLOWING' ||
          recurrenceScope === 'ALL_EVENTS';
        const eventPayload = isBulkScope
          ? { ...response.data, _recurrenceScope: recurrenceScope }
          : response.data;
        this.modalDataService.emitEvent(
          isBulkScope ? 'bulk-update' : 'update',
          eventPayload,
        );
        return { ...response, bulkRecurrenceUpdate: isBulkScope };
      }
    }
  }

  /**
   * Mark lead as lost — returns true on success, false on failure
   * Called BEFORE activity completion so we can block if backend rejects
   */
  async markLeadAsLost(leadId: number, lostReasonId: number): Promise<boolean> {
    console.log('📤 markLeadAsLost called:', { leadId, lostReasonId });
    try {
      const response = await firstValueFrom(
        this.leadsService.markLeadAsLost(leadId, lostReasonId)
      );
      console.log('📥 markLeadAsLost API response:', response);

      if (response?.success) {
        const updatedLead = await firstValueFrom(this.leadsService.getOne(leadId));
        console.log('📣 Emitting lead update:', updatedLead);
        this.modalDataService.emitLeadUpdate(updatedLead);
        return true;
      }

      // Unexpected response format
      this.notificationService.showNonErrorSnackBar(
        response?.message || 'Cannot mark this lead as Lost', 3000
      );
      return false;
    } catch (error: any) {
      const msg = error?.error?.message || error?.message || 'Cannot mark this lead as Lost';
      console.error('❌ Error marking lead as lost:', msg);
      this.notificationService.showNonErrorSnackBar(msg, 3000);
      return false;
    }
  }
  
  /**
   * Handles rescheduling an appointment via the reschedule API
   */
  async handleReschedule(activity: any, rescheduleData: any, eventForm?: any): Promise<any> {
    const toIntId = (value: any): number | null => {
      if (value === null || value === undefined || value === '') return null;
      if (typeof value === 'object' && value?.id != null) return toIntId(value.id);
      return typeof value === 'string' ? parseInt(value, 10) : value;
    };

    // Get company_id from activity, fall back to localStorage user data
    let companyId = toIntId(activity.company_id);
    if (!companyId) {
      const user = JSON.parse(localStorage.getItem('userData') || 'null');
      companyId = user?.company_id || null;
    }

    const reschedulePayload = {
      originalTaskId: toIntId(activity.id),
      originalTask: {
        id: toIntId(activity.id),
        company_id: companyId
      },
      newTask: {
        title: activity.title || activity.name,
        notes: activity.notes || '',
        company_id: companyId,
        startDate: rescheduleData.startDate,
        endDate: rescheduleData.endDate,
        status: 'todo',
        completed: false,
        type: toIntId(activity.type?.id || activity.type_id),
        lead: toIntId(activity.lead?.id || activity.lead_id || activity.lead),
        assignedTo: toIntId(activity.assigned_to_id || activity.assignedTo),
        campus: toIntId(activity.campus_id || activity.campus),
        allDay: activity.allDay || false,
        meetingLink: activity.meetingLink || '',
        meetingOwner: activity.meetingOwner || null,
        meetingName: activity.meetingName || '',
        meetingCode: activity.meetingCode || '',
        attendees: activity.attendees || [],
        attendanceStatus: 'SCHEDULED',
        appointment_category_id: toIntId(activity.appointment_category_id),
        subcategory_id: toIntId(activity.subcategory_id),
        rescheduleReason: rescheduleData.reason
      }
    };

    const response = await firstValueFrom(
      this.taskService.rescheduleAppointment(reschedulePayload)
    );

    this.modalDataService.emitEvent('create', response.data);

    if (eventForm) {
      return {
        action: 'rescheduled',
        formUpdated: true,
        newAppointment: response.data
      };
    }

    return {
      action: 'rescheduled',
      newAppointment: response.data
    };
  }

  /**
   * Validates if activity can be marked as done
   */
  canMarkAsDone(activity: any): boolean {
    // Add any business rules here
    return true;
  }
  
  /**
   * Gets the appropriate status when marking as done
   */
  getDefaultCompletionStatus(activity: any): string {
    if (activity?.type?.is_appointment) {
      return activity.attendanceStatus === 'SCHEDULED' ? 'SHOW' : activity.attendanceStatus;
    }
    return activity.status;
  }
}